    import { useEffect, useState } from "react";
import {
    Eye, X, Search, ArrowLeft, FileText,
    CheckCircle, XCircle, Clock, BarChart2
} from "lucide-react";
import Swal from "sweetalert2";
import config from "../../config";

export default function ClosedTickets({ handlePageChange }) {
    const [tickets, setTickets] = useState([]);
    const [search, setSearch] = useState("");
    const [showModal, setShowModal] = useState(false);
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [filterStatus, setFilterStatus] = useState("All");

    const API_TICKETS = `${config.API_BASE_URL}/Tickets`;

    const loadTickets = async () => {
        try {
            const res = await fetch(API_TICKETS);
            if (!res.ok) throw new Error();
            const data = await res.json();
            // Only Resolved and Closed
            setTickets(data.filter((t) => t.status === "Resolved" || t.status === "Closed"));
        } catch {
            Swal.fire("Error", "Failed to load tickets", "error");
        }
    };

    useEffect(() => {
        loadTickets();
    }, []);

    const openViewModal = (ticket) => {
        setSelectedTicket(ticket);
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setSelectedTicket(null);
    };

    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        return new Date(dateString).toLocaleString();
    };

    const getStatusColor = (status) => {
        const colors = {
            Resolved: "bg-green-500/20 text-green-400 border-green-500/30",
            Closed: "bg-gray-500/20 text-gray-400 border-gray-500/30",
        };
        return colors[status] || "bg-slate-500/20 text-slate-400 border-slate-500/30";
    };

    const getPriorityColor = (priority) => {
        const colors = {
            Low: "bg-green-500/20 text-green-400 border-green-500/30",
            Medium: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
            High: "bg-orange-500/20 text-orange-400 border-orange-500/30",
            Critical: "bg-red-500/20 text-red-400 border-red-500/30",
        };
        return colors[priority] || "bg-slate-500/20 text-slate-400 border-slate-500/30";
    };

    const filteredTickets = tickets.filter((ticket) => {
        const matchesSearch =
            ticket.ticketNumber?.toLowerCase().includes(search.toLowerCase()) ||
            ticket.subject?.toLowerCase().includes(search.toLowerCase()) ||
            ticket.name?.toLowerCase().includes(search.toLowerCase());
        const matchesStatus = filterStatus === "All" || ticket.status === filterStatus;
        return matchesSearch && matchesStatus;
    });

    const resolvedCount = tickets.filter((t) => t.status === "Resolved").length;
    const closedCount = tickets.filter((t) => t.status === "Closed").length;

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-slate-100 p-6">
            <div className="max-w-8xl mx-auto">

                {/* HEADER */}
                <div className="flex justify-between items-center mb-8">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => handlePageChange("dashboard")}
                            className="p-2.5 rounded-xl bg-slate-800/80 hover:bg-slate-700 border border-slate-700/50 transition-all"
                        >
                            <ArrowLeft size={20} />
                        </button>
                        <div>
                            <h1 className="text-3xl font-bold bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent">
                                Closed Tickets
                            </h1>
                            <p className="text-slate-400 text-sm mt-1">Resolved and closed support tickets history</p>
                        </div>
                    </div>
                </div>

                {/* STATS */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className="bg-slate-800/40 border border-green-500/20 rounded-2xl p-4 flex items-center gap-4">
                        <div className="p-3 bg-green-500/20 rounded-xl">
                            <CheckCircle className="text-green-400" size={22} />
                        </div>
                        <div>
                            <p className="text-slate-400 text-sm">Resolved</p>
                            <p className="text-2xl font-bold text-green-400">{resolvedCount}</p>
                        </div>
                    </div>
                    <div className="bg-slate-800/40 border border-gray-500/20 rounded-2xl p-4 flex items-center gap-4">
                        <div className="p-3 bg-gray-500/20 rounded-xl">
                            <XCircle className="text-gray-400" size={22} />
                        </div>
                        <div>
                            <p className="text-slate-400 text-sm">Closed</p>
                            <p className="text-2xl font-bold text-gray-400">{closedCount}</p>
                        </div>
                    </div>
                    <div className="bg-slate-800/40 border border-slate-500/20 rounded-2xl p-4 flex items-center gap-4">
                        <div className="p-3 bg-slate-500/20 rounded-xl">
                            <BarChart2 className="text-slate-400" size={22} />
                        </div>
                        <div>
                            <p className="text-slate-400 text-sm">Total Closed</p>
                            <p className="text-2xl font-bold text-slate-300">{tickets.length}</p>
                        </div>
                    </div>
                </div>

                {/* FILTERS */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div className="relative">
                        <Search className="absolute left-4 top-3.5 text-slate-400" size={18} />
                        <input
                            className="w-full pl-12 pr-4 py-3 bg-slate-800/60 border border-slate-700/50 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500/50 transition-all"
                            placeholder="Search tickets..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                    <select
                        className="px-4 py-3 bg-slate-800/60 border border-slate-700/50 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500/50 transition-all"
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                    >
                        <option value="All">All Status</option>
                        <option value="Resolved">Resolved</option>
                        <option value="Closed">Closed</option>
                    </select>
                </div>

                {/* TABLE */}
                <div className="bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-slate-700/50 overflow-hidden shadow-2xl">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-slate-800/80 border-b border-slate-700/50">
                                <tr>
                                    <th className="p-4 text-left text-sm font-semibold text-slate-300">Ticket #</th>
                                    <th className="p-4 text-left text-sm font-semibold text-slate-300">Subject</th>
                                    <th className="p-4 text-left text-sm font-semibold text-slate-300">Requester</th>
                                    <th className="p-4 text-left text-sm font-semibold text-slate-300">Assignee</th>
                                    <th className="p-4 text-left text-sm font-semibold text-slate-300">Team</th>
                                    <th className="p-4 text-center text-sm font-semibold text-slate-300">Priority</th>
                                    <th className="p-4 text-center text-sm font-semibold text-slate-300">Status</th>
                                    <th className="p-4 text-left text-sm font-semibold text-slate-300">Created</th>
                                    <th className="p-4 text-center text-sm font-semibold text-slate-300">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredTickets.map((ticket) => (
                                    <tr key={ticket.ticketNumber} className="border-b border-slate-700/30 hover:bg-slate-700/20 transition-colors opacity-90">
                                        <td className="p-4">
                                            <span className="font-mono text-green-400 font-medium">{ticket.ticketNumber}</span>
                                        </td>
                                        <td className="p-4 max-w-xs">
                                            <div className="truncate font-medium">{ticket.subject}</div>
                                            <div className="text-xs text-slate-400 truncate">{ticket.category}</div>
                                        </td>
                                        <td className="p-4">
                                            <div className="font-medium">{ticket.name}</div>
                                            <div className="text-xs text-slate-400">{ticket.email}</div>
                                        </td>
                                        <td className="p-4">
                                            {ticket.assignees ? (
                                                <span className="px-2 py-1 bg-slate-600/40 text-slate-300 rounded text-xs">{ticket.assignees}</span>
                                            ) : (
                                                <span className="text-slate-500 text-xs italic">Unassigned</span>
                                            )}
                                        </td>
                                        <td className="p-4">
                                            <span className="text-sm text-slate-300">{ticket.team || "—"}</span>
                                        </td>
                                        <td className="p-4 text-center">
                                            <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getPriorityColor(ticket.priority)}`}>
                                                {ticket.priority || "N/A"}
                                            </span>
                                        </td>
                                        <td className="p-4 text-center">
                                            <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(ticket.status)}`}>
                                                {ticket.status || "N/A"}
                                            </span>
                                        </td>
                                        <td className="p-4 text-sm text-slate-300">{formatDate(ticket.createdAt)}</td>
                                        <td className="p-4 text-center">
                                            <button
                                                onClick={() => openViewModal(ticket)}
                                                className="p-2 rounded-lg bg-green-500/10 text-green-400 hover:bg-green-500/20 transition-all"
                                                title="View"
                                            >
                                                <Eye size={16} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                {filteredTickets.length === 0 && (
                                    <tr>
                                        <td colSpan="9" className="text-center p-12 text-slate-400">
                                            <CheckCircle size={48} className="mx-auto mb-3 opacity-30" />
                                            <p className="text-lg">No closed tickets found</p>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* VIEW MODAL (Read-Only for Closed Tickets) */}
            {showModal && selectedTicket && (
                <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-slate-800 rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto relative shadow-2xl border border-slate-700/50">
                        <div className="sticky top-0 bg-slate-800 border-b border-slate-700/50 p-6 flex justify-between items-center z-10">
                            <div>
                                <h2 className="text-2xl font-bold bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent">
                                    Ticket Details
                                </h2>
                                <p className="text-slate-400 text-sm mt-1">{selectedTicket.ticketNumber}</p>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className={`px-3 py-1.5 rounded-full text-xs font-medium border ${getStatusColor(selectedTicket.status)}`}>
                                    {selectedTicket.status}
                                </span>
                                <button onClick={closeModal} className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700 transition-all">
                                    <X size={20} />
                                </button>
                            </div>
                        </div>

                        <div className="p-6 space-y-6">
                            {/* Quick Info */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                <div className="p-3 bg-slate-900/50 rounded-xl border border-slate-700/50 text-center">
                                    <p className="text-xs text-slate-400 mb-1">Priority</p>
                                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${getPriorityColor(selectedTicket.priority)}`}>
                                        {selectedTicket.priority || "N/A"}
                                    </span>
                                </div>
                                <div className="p-3 bg-slate-900/50 rounded-xl border border-slate-700/50 text-center">
                                    <p className="text-xs text-slate-400 mb-1">Assignee</p>
                                    <p className="text-sm text-slate-200 truncate">{selectedTicket.assignees || "—"}</p>
                                </div>
                                <div className="p-3 bg-slate-900/50 rounded-xl border border-slate-700/50 text-center">
                                    <p className="text-xs text-slate-400 mb-1">Team</p>
                                    <p className="text-sm text-slate-200 truncate">{selectedTicket.team || "—"}</p>
                                </div>
                                <div className="p-3 bg-slate-900/50 rounded-xl border border-slate-700/50 text-center">
                                    <p className="text-xs text-slate-400 mb-1">Origin</p>
                                    <p className="text-sm text-slate-200 truncate">{selectedTicket.origin || "—"}</p>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-3">
                                    <h3 className="font-semibold text-lg text-slate-200">Requester Info</h3>
                                    <InfoRow label="Name" value={selectedTicket.name} />
                                    <InfoRow label="Email" value={selectedTicket.email} />
                                    <InfoRow label="Contact" value={selectedTicket.contact} />
                                </div>
                                <div className="space-y-3">
                                    <h3 className="font-semibold text-lg text-slate-200">Ticket Info</h3>
                                    <InfoRow label="Category" value={selectedTicket.category} />
                                    <InfoRow label="Created" value={formatDate(selectedTicket.createdAt)} />
                                    <InfoRow label="Deadline" value={formatDate(selectedTicket.deadline)} />
                                </div>
                            </div>

                            <div className="space-y-4">
                                <div>
                                    <h3 className="font-semibold text-lg text-slate-200 mb-2">Subject</h3>
                                    <div className="p-4 bg-slate-900/50 rounded-xl border border-slate-700/50">{selectedTicket.subject}</div>
                                </div>
                                <div>
                                    <h3 className="font-semibold text-lg text-slate-200 mb-2">Description</h3>
                                    <div className="p-4 bg-slate-900/50 rounded-xl border border-slate-700/50 max-h-40 overflow-y-auto">
                                        {selectedTicket.description || "No description"}
                                    </div>
                                </div>
                                {selectedTicket.notes && (
                                    <div>
                                        <h3 className="font-semibold text-lg text-slate-200 mb-2">Notes</h3>
                                        <div className="p-4 bg-slate-900/50 rounded-xl border border-slate-700/50">{selectedTicket.notes}</div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function InfoRow({ label, value }) {
    return (
        <div>
            <label className="text-sm text-slate-400 mb-1 block">{label}</label>
            <div className="px-3 py-2 bg-slate-900/50 rounded-lg border border-slate-700/50 text-sm">{value || "N/A"}</div>
        </div>
    );
}